import { styled, Theme } from '@mui/material';
import React, { FC } from 'react';

interface StyleParams {
	theme: Theme;
	selected: boolean;
	error?: string;
	company?: string;
}

interface LabelStyleParams {
	theme: Theme;
	selected: boolean;
	company?: string;
}

const company = import.meta.env.VITE_AUTH_REALM?.toUpperCase();

const getRadioButtonStyles = ({ theme, selected, error, company }: StyleParams) => {
	const baseStyles = {
		cursor: 'pointer' as const,
		position: 'relative' as const,
		display: 'flex' as const,
		alignItems: 'center' as const,
		justifyContent: 'center' as const,
		width: '5rem',
		height: '5rem',
		borderRadius: '50%',
	};

	const getBorderColor = (): string => {
		if (error === 'true') return theme.palette.error.main;
		if (selected) {
			return company === 'ACORN'
				? theme.palette.secondary.main || theme.palette.secondary.light
				: theme.palette.primary.main;
		}
		return theme.palette.grey[500];
	};

	const getBackgroundColor = (): string => {
		if (!selected) return 'white';
		return company === 'ACORN'
			? theme.palette.secondary.main || theme.palette.secondary.light
			: theme.palette.primary.main;
	};

	const getHoverStyles = () => {
		const borderColorMap: Record<string, string> = {
			ACORN: theme.palette.secondary.light,
			MOTORCADE: theme.palette.primary.main,
		};
		const borderColor = borderColorMap[company ?? ''] ?? theme.palette.primary.light;
		const backgroundColor = selected
			? company === 'ACORN'
				? theme.palette.secondary.light
				: theme.palette.primary.light
			: 'white';

		return {
			border: `2px solid ${borderColor}`,
			backgroundColor,
			'&:hover > span': {
				color: selected ? theme.palette.primary.main : theme.palette.primary.main,
			},
		};
	};

	return {
		...baseStyles,
		border: `2px solid ${getBorderColor()}`,
		backgroundColor: getBackgroundColor(),
		'&:hover': getHoverStyles(),
	};
};

const getLabelStyles = ({ theme, selected }: LabelStyleParams) => {
	const getTextColor = (): string => {
		if (!selected) return theme.palette.common.black;
		return theme.palette.common.white;
	};

	return {
		...theme.typography.button,
		position: 'absolute' as const,
		color: getTextColor(),
	};
};

const Body = styled('label')(() => ({}));

const Container = styled('span')<{ selected: boolean; error?: string }>`
	${({ theme, selected, error }) => {
		return getRadioButtonStyles({ theme, selected, error, company });
	}}
`;

const Input = styled('input')`
	opacity: 0;
	width: 0;
	height: 0;
	position: absolute;
	visibility: visible;

	&:focus {
		outline: none;
	}
`;

const Label = styled('span')<{ selected: boolean }>`
	${({ theme, selected }) => {
		return getLabelStyles({ theme, selected, company });
	}}
`;

interface RadioButtonProps {
	label: string;
	selectedValue: string;
	value: string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	error?: boolean;
	id: string;
	name?: string;
}

const RadioButton: FC<RadioButtonProps> = ({
	label,
	selectedValue,
	value,
	onChange,
	onBlur,
	error,
	id,
	name = 'radio-button',
}) => (
	<Body role='radio' htmlFor={`${id}-radio`}>
		<Container selected={selectedValue === value} error={error ? 'true' : undefined}>
			<Input
				id={`${id}-radio`}
				type='radio'
				checked={selectedValue === value}
				onChange={onChange}
				onBlur={onBlur}
				value={value}
				name={name}
			/>
			<Label selected={selectedValue === value}>{label}</Label>
		</Container>
	</Body>
);

export default RadioButton;
